/**
 * Native survey builder — domain types (VER-108 → VER-109 full-power builder).
 *
 * VerbalMono builds its own in-platform survey constructor instead of
 * SurveyMonkey. VER-109 extends the original single-shared-Likert model to a
 * full-power builder:
 *
 *   - ordered {@link Section}s, each with its own ordered {@link SurveyQuestion}s
 *   - six {@link QuestionType}s (likert / single / multi / freeText / vas / nps)
 *   - per-question scoring config (reverse for rating types; `scored` opt-in for
 *     multi/freeText; numeric types contribute to the sum)
 *   - {@link BranchingRule}s that show / hide a question or section based on a
 *     prior numeric answer
 *   - a draft → published lifecycle with an integer version
 *
 * BACKWARDS COMPATIBILITY: the WHO-5 / UCLA-20 reference scales migrate to a
 * single all-`likert` section with a shared {@link SurveyScale}; their scored
 * behaviour is byte-for-byte unchanged (see scoring.ts + scoring.fixtures.ts).
 *
 * PERSISTENCE: the whole {@link SurveyDefinition} is stored server-side as an
 * opaque JSON `definition` blob. The top-level fields `code`, `title`,
 * `instructions`, `scaleCode`, `higherIsBetter`, `status` and `version` are the
 * promoted columns the backend lifts out of that blob — keep them top-level.
 */

/** The kinds of question the builder can render + (optionally) score. */
export type QuestionType =
    /** Rating grid over a shared/own numeric scale (WHO-5 style). Scored, reversible. */
    | "likert"
    /** Single-choice radio list over numeric options. Scored, reversible. */
    | "single"
    /** Multiple-choice checkboxes over numeric options. Non-scored by default. */
    | "multi"
    /** Free-text answer. Never scored. */
    | "freeText"
    /** Visual analogue slider across a numeric [min,max]. Scored, reversible. */
    | "vas"
    /** 0–10 net-promoter style rating. Scored, reversible. */
    | "nps";

/** Question types whose answer is a single number the scorer can sum. */
export const NUMERIC_SINGLE_TYPES: ReadonlyArray<QuestionType> = ["likert", "single", "vas", "nps"];

/** Question types that render a fixed/own list of selectable numeric options. */
export const CHOICE_TYPES: ReadonlyArray<QuestionType> = ["likert", "single", "multi"];

/** One selectable point on a Likert / choice scale, e.g. { label: "Often", value: 4 }. */
export type ScaleOption = {
    label: string;
    value: number;
};

/** A shared Likert scale that choice questions inherit when they define no own options. */
export type SurveyScale = {
    /** Ordered left→right as rendered. Values need not be contiguous (WHO-5 is 0–5). */
    options: ScaleOption[];
};

/** Config for a visual-analogue slider question. */
export type VasConfig = {
    min: number;
    max: number;
    /** Optional anchor labels shown at each end of the track. */
    minLabel?: string;
    maxLabel?: string;
    /** Slider step (defaults to 1 when unset). */
    step?: number;
};

/** Config for a free-text question. */
export type FreeTextConfig = {
    multiline: boolean;
    maxLength?: number;
    placeholder?: string;
};

export type SurveyQuestion = {
    /** Stable key used for response storage + scoring, e.g. "q1". Never reorder-derived. */
    id: string;
    /** Display position (1-based) within its section. */
    order: number;
    text: string;
    /** Render + scoring behaviour. Defaults to "likert" for migrated seeds. */
    type: QuestionType;
    /**
     * When true, a rating value is flipped before summing:
     * reversed = (min + max) - value, where [min,max] is the question's effective
     * numeric range. Only meaningful for likert / single / vas / nps.
     */
    reverseScored: boolean;
    /**
     * Whether this question contributes to the survey sum. Numeric single-answer
     * types default to scored; multi is opt-in; freeText can never be scored.
     * Stored explicitly so the intent survives type changes.
     */
    scored: boolean;
    /** Whether an answer is required for the survey to be "complete". Defaults true. */
    required: boolean;
    /**
     * Own numeric options for choice types (likert / single / multi). When absent,
     * likert / single inherit the survey's shared {@link SurveyScale}. `multi`
     * must always define its own options.
     */
    options?: ScaleOption[];
    /** VAS slider config (type === "vas"). */
    vas?: VasConfig;
    /** Free-text config (type === "freeText"). */
    freeText?: FreeTextConfig;
};

/** An ordered group of questions shown together with a heading. */
export type Section = {
    id: string;
    /** Display position (1-based) within the survey. */
    order: number;
    title: string;
    description?: string;
    questions: SurveyQuestion[];
};

/** Comparison used by a branching rule against a prior numeric answer. */
export type BranchOperator = "eq" | "neq" | "gt" | "gte" | "lt" | "lte";

/**
 * What a satisfied rule does to its target.
 *  - "show": the target is HIDDEN by default and revealed only when the rule matches.
 *  - "hide": the target is VISIBLE by default and hidden when the rule matches.
 */
export type BranchAction = "show" | "hide";

export type BranchTargetType = "question" | "section";

/**
 * Conditional visibility: when `sourceQuestion`'s numeric answer satisfies
 * `operator value`, apply `action` to the `target`. Rules referencing an
 * unanswered source question do not fire (the condition is treated as unmet).
 */
export type BranchingRule = {
    id: string;
    sourceQuestionId: string;
    operator: BranchOperator;
    /** Numeric comparison value (choice value / slider value / nps score). */
    value: number;
    action: BranchAction;
    targetType: BranchTargetType;
    targetId: string;
};

export type SurveyScoring = {
    /** MVP supports a continuous sum of (reverse-adjusted) scored item values. */
    method: "sum";
    /** Optional post-sum multiplier (WHO-5 ×4 → 0–100%). */
    multiplier?: number;
    /** Inclusive [min, max] of the raw summed score, for display + validation. */
    rawRange: [number, number];
    /**
     * Optional clinical cut-off band rendered as guidance (never a diagnosis).
     * `belowValue` is compared against the FINAL score (after any multiplier).
     */
    cutoff?: {
        belowValue: number;
        label: string;
    };
};

/** Publication lifecycle — only "published" surveys are offered in the assess flow. */
export type SurveyStatus = "draft" | "published";

export type SurveyDefinition = {
    id: string;
    /** Short machine code, e.g. "WHO5", "UCLA20". Unique. Promoted column. */
    code: string;
    title: string;
    instructions: string;
    /**
     * Optional link to a known clinical scale in the scale catalogue (e.g. "WHO5").
     * Promoted column; free-built surveys leave it undefined.
     */
    scaleCode?: string;
    /**
     * Direction of the scale for reporting: true when a HIGHER score is better
     * (WHO-5), false when higher is worse (UCLA loneliness). Promoted column.
     */
    higherIsBetter: boolean;
    /** draft | published. Promoted column. */
    status: SurveyStatus;
    /** Integer version, bumped on each publish. Promoted column (`currentVersion`). */
    version: number;
    /** Shared Likert scale inherited by choice questions without their own options. */
    defaultScale: SurveyScale;
    /** Ordered sections; each holds its own ordered questions. */
    sections: Section[];
    scoring: SurveyScoring;
    /** Conditional show/hide rules. Empty when the survey is linear. */
    branchingRules: BranchingRule[];
    /** Citation/licence line that MUST be displayed when the survey is shown. */
    licence?: string;
    /**
     * true = shipped reference scale (read-only template; clone to edit).
     * Editable user-built surveys are false.
     */
    isSystemSeed: boolean;
};

/**
 * A respondent's answers, keyed by question id. The value shape depends on the
 * question type:
 *   - likert / single / vas / nps → a single number
 *   - multi                       → an array of selected numbers
 *   - freeText                    → a string
 *   - unanswered                  → "" (empty string sentinel)
 */
export type SurveyResponseValue = number | string | number[] | "";
export type SurveyResponseValues = Record<string, SurveyResponseValue>;

/** Fixed 0–10 option set for NPS questions (built once, never mutated). */
export const NPS_OPTIONS: ScaleOption[] = Array.from({ length: 11 }, (_, i) => ({
    label: String(i),
    value: i,
}));

/** All questions across all sections, flattened in section-order then question-order. */
export const allQuestions = (survey: SurveyDefinition): SurveyQuestion[] =>
    [...survey.sections]
        .sort((a, b) => a.order - b.order)
        .flatMap((section) => [...section.questions].sort((a, b) => a.order - b.order));

/** Look up a question by id anywhere in the survey. */
export const findQuestion = (survey: SurveyDefinition, questionId: string): SurveyQuestion | undefined =>
    allQuestions(survey).find((q) => q.id === questionId);

/**
 * The numeric options a choice/rating question actually renders + scores against.
 *  - nps → the fixed 0–10 set
 *  - likert / single / multi → own options, else the survey's shared scale
 *  - vas / freeText → no discrete options (empty)
 */
export const effectiveOptions = (survey: SurveyDefinition, question: SurveyQuestion): ScaleOption[] => {
    if (question.type === "nps") {
        return NPS_OPTIONS;
    }
    if (question.type === "vas" || question.type === "freeText") {
        return [];
    }
    if (question.options && question.options.length > 0) {
        return question.options;
    }
    return survey.defaultScale.options;
};

/**
 * The [min,max] numeric range a question's answer can take, used for reverse
 * scoring and range display. VAS uses its configured bounds; choice/nps use their
 * effective options; freeText has no numeric range ([0,0]).
 */
export const questionBounds = (survey: SurveyDefinition, question: SurveyQuestion): { min: number; max: number } => {
    if (question.type === "vas" && question.vas) {
        return { min: question.vas.min, max: question.vas.max };
    }
    const options = effectiveOptions(survey, question);
    if (options.length === 0) {
        return { min: 0, max: 0 };
    }
    const values = options.map((o) => o.value);
    return { min: Math.min(...values), max: Math.max(...values) };
};

/** Whether a question type can ever contribute a number to the score. */
export const isScorable = (type: QuestionType): boolean => type !== "freeText";
